/**
 * Cost By Provider Chart - Visual breakdown of costs per API provider.
 */

import type { ProviderCostItem } from '../../types';

interface CostByProviderChartProps {
  data: ProviderCostItem[] | undefined;
  isLoading: boolean;
  error: Error | null;
}

const PROVIDER_COLORS: Record<string, string> = {
  anthropic: 'bg-orange-500',
  brave_search: 'bg-blue-500',
  newsapi: 'bg-green-500',
  proxycurl: 'bg-purple-500',
  sec_edgar: 'bg-yellow-500',
  yahoo_finance: 'bg-primary-500',
  rss_feeds: 'bg-teal-500',
  linkedin_scraper: 'bg-sky-500',
  glassdoor: 'bg-lime-500',
  scrapy: 'bg-rose-500',
};

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic (Claude)',
  brave_search: 'Brave Search',
  newsapi: 'NewsAPI',
  proxycurl: 'Proxycurl',
  sec_edgar: 'SEC EDGAR',
  yahoo_finance: 'Yahoo Finance',
  rss_feeds: 'RSS Feeds',
  linkedin_scraper: 'LinkedIn Scraper',
  glassdoor: 'Glassdoor',
  scrapy: 'Scrapy',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function ProviderBar({
  provider,
  cost,
  calls,
  percentage,
}: {
  provider: string;
  cost: number;
  calls: number;
  percentage: number;
}) {
  const colorClass = PROVIDER_COLORS[provider] || 'bg-gray-500';
  const label = PROVIDER_LABELS[provider] || provider;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500">
          {formatCurrency(cost)} ({formatNumber(calls)} calls)
        </span>
      </div>
      <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClass} transition-all duration-500 ease-out`}
          style={{ width: `${Math.max(percentage, 1)}%` }}
        />
      </div>
    </div>
  );
}

export function CostByProviderChart({ data, isLoading, error }: CostByProviderChartProps) {
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">Failed to load provider costs: {error.message}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Cost by Provider</h3>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-1 animate-pulse">
              <div className="flex justify-between">
                <div className="h-4 bg-gray-200 rounded w-24" />
                <div className="h-4 bg-gray-200 rounded w-20" />
              </div>
              <div className="h-4 bg-gray-200 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Cost by Provider</h3>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <p className="text-gray-500">No API usage data available yet.</p>
        </div>
      </div>
    );
  }

  // Calculate total for percentages
  const totalCost = data.reduce((sum, item) => sum + item.total_cost_usd, 0);

  // Sort by cost descending
  const sortedData = [...data].sort((a, b) => b.total_cost_usd - a.total_cost_usd);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Cost by Provider</h3>
        <span className="text-sm text-gray-500">Total: {formatCurrency(totalCost)}</span>
      </div>

      <div className="space-y-4">
        {sortedData.map((item) => (
          <ProviderBar
            key={item.provider}
            provider={item.provider_display_name || item.provider}
            cost={item.total_cost_usd}
            calls={item.total_calls}
            percentage={(item.total_cost_usd / totalCost) * 100}
          />
        ))}
      </div>
    </div>
  );
}

export default CostByProviderChart;
